import { AnalyticsService } from '@alison/analytics/client';
import { InitialsAvatar } from '@alison/ui';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useRef, useState } from 'react';
import { initialsFrom } from '../../../lib/format';
import { CreativeIdLine } from '../../creatives/CreativeIdLine';
import { MetricsDensityToolbar } from '../../creatives/MetricsDensityToolbar';
import { NetworkBadge } from '../../creatives/NetworkBadge';
import { TableEmptyState } from '../../creatives/TableEmptyState';
import {
  ELLIPSIS_SX,
  type MetricsMode,
  NO_CREATIVES_MESSAGE,
  scaleWithDensity,
  TOOLBAR_BUTTON_SX,
  usePagedList,
} from '../../creatives/utils';
import { TaggedChip } from './CompetitionCreativesTableCells';
import type { CompetitionCreativeRow, CompetitionCreativesData } from './data/api';
import { competitionCreativeSubline, formatDateAdded, formatLength } from './utils';

const PAGE_SIZE = 50;
const DEFAULT_DENSITY = 60;
const SKELETON_COUNT = 8;
const META_PANEL_HEIGHT = 60;

const CARD_SX = {
  borderRadius: 1,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  border: '2px solid',
  borderColor: 'transparent',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
};

interface CompetitionCreativesGridProps {
  data: CompetitionCreativesData | undefined;
  pending: boolean;
  onOpen: (creativeId: string) => void;
}

function gridSx(density: number) {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${scaleWithDensity(density, 220, 380)}px, 1fr))`,
    gap: 4,
    transition: 'grid-template-columns 150ms ease',
  };
}

function MetaTile({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={(theme) => ({ display: 'block', color: alpha(theme.palette.common.white, 0.6) })}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'common.white' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function CreativeCard({
  creative,
  thumbnailHeight,
  metricsMode,
  onOpen,
}: {
  creative: CompetitionCreativeRow;
  thumbnailHeight: number;
  metricsMode: MetricsMode;
  onOpen: (creativeId: string) => void;
}): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const showMeta = metricsMode === 'fixed' || hovered;
  const isImage = creative.creativeType?.toLowerCase() === 'image';
  const hasVideoPreview = Boolean(creative.previewUrl) && !isImage;
  const subline = competitionCreativeSubline(creative);
  const initials = initialsFrom(creative.competitorName);

  function handleMouseEnter(): void {
    setHovered(true);
    if (!hasVideoPreview) return;
    setIsPlaying(true);
    videoRef.current?.play().catch(() => setIsPlaying(false));
  }

  function handleMouseLeave(): void {
    setHovered(false);
    setIsPlaying(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onOpen(creative.creativeId)}
      sx={(theme) => ({
        ...CARD_SX,
        borderColor:
          hovered && metricsMode === 'hover' ? theme.palette.primary.main : 'transparent',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        },
      })}
    >
      <Box
        sx={{
          position: 'relative',
          height: thumbnailHeight,
          overflow: 'hidden',
          bgcolor: 'background.page',
          transition: 'height 150ms ease',
        }}
      >
        {thumbnailFailed || creative.thumbnailUrl === null ? null : (
          <Box
            component="img"
            src={creative.thumbnailUrl ?? undefined}
            alt=""
            onError={() => setThumbnailFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {hasVideoPreview && (
          <Box
            component="video"
            ref={videoRef}
            src={creative.previewUrl ?? undefined}
            muted
            loop
            playsInline
            preload="none"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isPlaying ? 1 : 0,
              transition: 'opacity 150ms ease',
              pointerEvents: 'none',
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          <NetworkBadge network={creative.network} />
          {subline === '' ? null : (
            <Typography
              variant="overline"
              sx={(theme) => ({
                px: 1,
                py: 0.25,
                borderRadius: 0.6,
                bgcolor: alpha(theme.palette.common.black, 0.6),
                color: 'common.white',
              })}
            >
              {subline}
            </Typography>
          )}
        </Box>
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <TaggedChip tagged={creative.tagged} />
        </Box>
        {hasVideoPreview && !isPlaying && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'background.paper',
              }}
            >
              <PlayArrowRounded sx={{ fontSize: 22, color: 'text.primary' }} />
            </Box>
          </Box>
        )}
        <Box
          sx={(theme) => ({
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 1)} 0%, ${alpha(theme.palette.common.black, 0.85)} 60%, ${alpha(theme.palette.common.black, 0)} 100%)`,
            pt: 12,
          })}
        >
          <Box
            sx={{
              overflow: 'hidden',
              maxHeight: showMeta ? META_PANEL_HEIGHT : 0,
              transition: 'max-height 200ms ease',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, px: 2 }}>
              <MetaTile
                label="Days running"
                value={creative.activeDays === null ? '—' : String(creative.activeDays)}
              />
              <MetaTile label="Length" value={formatLength(creative.duration)} />
              <MetaTile label="Date added" value={formatDateAdded(creative.firstSeenDate)} />
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 2 }}>
            <Box sx={{ mb: 0.5 }}>
              <CreativeIdLine creativeId={creative.creativeId} onDark />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              {creative.logoUrl === null && initials === null ? (
                <Box sx={{ width: 28, height: 28, flexShrink: 0 }} />
              ) : (
                <InitialsAvatar
                  variant="rounded"
                  size={28}
                  initials={initials ?? ''}
                  src={creative.logoUrl ?? undefined}
                />
              )}
              <Typography sx={{ ...ELLIPSIS_SX, color: 'common.white', fontWeight: 700 }}>
                {creative.competitorName ?? 'Unknown'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SkeletonCard({ thumbnailHeight }: { thumbnailHeight: number }): ReactNode {
  return (
    <Box sx={CARD_SX}>
      <Skeleton variant="rectangular" sx={{ height: thumbnailHeight }} />
    </Box>
  );
}

export function CompetitionCreativesGrid({
  data,
  pending,
  onOpen,
}: CompetitionCreativesGridProps): ReactNode {
  const [metricsMode, setMetricsMode] = useState<MetricsMode>('hover');
  const [density, setDensity] = useState(DEFAULT_DENSITY);

  const creatives = data?.creatives ?? [];
  const isLoading = pending && data === undefined;
  const thumbnailHeight = scaleWithDensity(density, 140, 300);
  const {
    visibleItems: visibleCreatives,
    hasMore,
    loadMore,
  } = usePagedList(data, creatives, PAGE_SIZE);

  return (
    <Box>
      <MetricsDensityToolbar
        metricsMode={metricsMode}
        onMetricsModeChange={setMetricsMode}
        density={density}
        onDensityChange={setDensity}
      />
      {isLoading ? (
        <Box sx={gridSx(density)}>
          {Array.from({ length: SKELETON_COUNT }, (_, index) => `skeleton-${index}`).map((id) => (
            <SkeletonCard key={id} thumbnailHeight={thumbnailHeight} />
          ))}
        </Box>
      ) : data !== undefined && creatives.length === 0 ? (
        <Box sx={{ py: 5 }}>
          <TableEmptyState>{NO_CREATIVES_MESSAGE}</TableEmptyState>
        </Box>
      ) : (
        <Box data-test="competition-creatives-cards" sx={gridSx(density)}>
          {visibleCreatives.map((creative) => (
            <CreativeCard
              key={creative.creativeId}
              creative={creative}
              thumbnailHeight={thumbnailHeight}
              metricsMode={metricsMode}
              onOpen={onOpen}
            />
          ))}
        </Box>
      )}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <Button
            variant="outlined"
            data-test="competition-creatives-load-more"
            onClick={() => {
              AnalyticsService.track('Competition Creatives - More Loaded');
              loadMore();
            }}
            sx={TOOLBAR_BUTTON_SX}
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}
